// src/components/profile-input/steps/PreferencesStep.jsx
import React, { useState, useEffect, useRef } from "react";
import { Grid, Typography, Divider, Stack, Box, MenuItem, Select, InputAdornment } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { TextInput } from "../FormFields";
import { options } from "../formUtils";

const PreferencesStep = ({
  formData,
  handleInputChange,
  profPrefInput,
  setProfPrefInput,
  locPrefInput,
  setLocPrefInput,
  handleArrayAddProfPref,
  handleArrayRemoveProfPref,
  handleArrayAddLocPref,
  handleArrayRemoveLocPref
}) => {
  const theme = useTheme();

  // State for smart dropdown fields
  const [maritalStatusPrefInput, setMaritalStatusPrefInput] = useState("");
  const [showMaritalStatusPrefOptions, setShowMaritalStatusPrefOptions] = useState(false);

  const [smokingPrefInput, setSmokingPrefInput] = useState("");
  const [showSmokingPrefOptions, setShowSmokingPrefOptions] = useState(false);

  const [drinkingPrefInput, setDrinkingPrefInput] = useState("");
  const [showDrinkingPrefOptions, setShowDrinkingPrefOptions] = useState(false);

  const [religionPrefInput, setReligionPrefInput] = useState("");
  const [showReligionPrefOptions, setShowReligionPrefOptions] = useState(false);

  const [countryPrefInput, setCountryPrefInput] = useState("");
  const [showCountryPrefOptions, setShowCountryPrefOptions] = useState(false);

  // State for minimum height field
  const [minHeightUnit, setMinHeightUnit] = useState("feet");
  const [minHeightDisplayValue, setMinHeightDisplayValue] = useState("");
  const [minHeightError, setMinHeightError] = useState("");

  // Refs for dropdown containers
  const maritalStatusPrefRef = useRef(null);
  const smokingPrefRef = useRef(null);
  const drinkingPrefRef = useRef(null);
  const religionPrefRef = useRef(null);
  const countryPrefRef = useRef(null);

  // Initialize from form data
  useEffect(() => {
    setMaritalStatusPrefInput(formData.preferences?.marital_status || "");
    setSmokingPrefInput(formData.preferences?.smoking_preference || "");
    setDrinkingPrefInput(formData.preferences?.drinking_preference || "");
    setReligionPrefInput(formData.preferences?.religion || "");
    setCountryPrefInput(formData.preferences?.country || "");

    // Initialize minimum height from form data
    const minHeightData = formData.preferences?.min_height || { value: "", unit: "feet" };
    setMinHeightDisplayValue(minHeightData.value || "");
    setMinHeightUnit(minHeightData.unit || "feet");
  }, [
    formData.preferences?.marital_status,
    formData.preferences?.smoking_preference,
    formData.preferences?.drinking_preference,
    formData.preferences?.religion,
    formData.preferences?.country,
    formData.preferences?.min_height
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = [
        { ref: maritalStatusPrefRef, setter: setShowMaritalStatusPrefOptions },
        { ref: smokingPrefRef, setter: setShowSmokingPrefOptions },
        { ref: drinkingPrefRef, setter: setShowDrinkingPrefOptions },
        { ref: religionPrefRef, setter: setShowReligionPrefOptions },
        { ref: countryPrefRef, setter: setShowCountryPrefOptions }
      ];

      dropdowns.forEach(({ ref, setter }) => {
        if (ref.current && !ref.current.contains(event.target)) {
          setter(false);
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ========== MINIMUM HEIGHT HANDLERS ==========

  // Handle minimum height unit change
  const handleMinHeightUnitChange = (e) => {
    const newUnit = e.target.value;
    setMinHeightUnit(newUnit);
    setMinHeightDisplayValue("");
    updateMinHeightInForm("", newUnit);
  };

  // Handle minimum height input
  const handleMinHeightChange = (e) => {
    const value = e.target.value;
    setMinHeightDisplayValue(value);
    setMinHeightError("");

    // Update form data immediately
    handleInputChange("preferences.min_height", {
      value: value,
      unit: minHeightUnit
    });
  };

  const updateMinHeightInForm = (value, unit) => {
    handleInputChange("preferences.min_height", {
      value: value,
      unit: unit
    });
  };

  // Validate minimum height on blur
  const validateMinHeight = () => {
    const value = minHeightDisplayValue;

    if (!value) {
      updateMinHeightInForm("", minHeightUnit);
      return false;
    }

    if (minHeightUnit === "feet") {
      // Try to parse feet'inches" format
      const feetInchesPattern = /^(\d{1,2})'(\d{1,2})"?$/;
      const feetInchesMatch = value.match(feetInchesPattern);

      if (feetInchesMatch) {
        const feet = parseInt(feetInchesMatch[1]);
        const inches = parseInt(feetInchesMatch[2]);

        if (feet < 3 || feet > 9) {
          setMinHeightError("Feet should be between 3 and 9");
          return false;
        }

        if (inches < 0 || inches > 11) {
          setMinHeightError("Inches should be between 0 and 11");
          return false;
        }

        if (feet === 9 && inches > 0) {
          setMinHeightError("Maximum height is 9'0\"");
          return false;
        }

        const finalValue = `${feet}'${inches}"`;
        updateMinHeightInForm(finalValue, "feet");
        setMinHeightDisplayValue(finalValue);
        setMinHeightError("");
        return true;
      }

      // Try to parse decimal format like 5.8, 5.10
      const decimalPattern = /^(\d{1,2})\.(\d{1,2})$/;
      const decimalMatch = value.match(decimalPattern);

      if (decimalMatch) {
        const feet = parseInt(decimalMatch[1]);
        const decimalPart = decimalMatch[2];

        // Convert decimal part to inches
        let inches;
        if (decimalPart.length === 2 && decimalPart.startsWith('1')) {
          inches = parseInt(decimalPart);
        } else if (decimalPart.length === 2) {
          inches = parseInt(decimalPart);
        } else if (decimalPart.length === 1) {
          inches = parseInt(decimalPart);
        } else {
          setMinHeightError("Invalid format. Use feet.inches (e.g., 5.8, 5.10)");
          return false;
        }

        if (isNaN(inches) || inches < 0 || inches > 11) {
          setMinHeightError("Inches should be between 0 and 11");
          return false;
        }

        if (feet < 3 || feet > 9) {
          setMinHeightError("Feet should be between 3 and 9");
          return false;
        }

        if (feet === 9 && inches > 0) {
          setMinHeightError("Maximum height is 9'0\"");
          return false;
        }

        const finalValue = `${feet}'${inches}"`;
        updateMinHeightInForm(finalValue, "feet");
        setMinHeightDisplayValue(finalValue);
        setMinHeightError("");
        return true;
      }

      // Try to parse simple number (just feet)
      const justFeetPattern = /^(\d{1,2})$/;
      const justFeetMatch = value.match(justFeetPattern);

      if (justFeetMatch) {
        const feet = parseInt(justFeetMatch[1]);

        if (feet < 3 || feet > 9) {
          setMinHeightError("Feet should be between 3 and 9");
          return false;
        }

        const finalValue = `${feet}'0"`;
        updateMinHeightInForm(finalValue, "feet");
        setMinHeightDisplayValue(finalValue);
        setMinHeightError("");
        return true;
      }

      setMinHeightError("Use format: feet'inches\" (e.g., 5'8\") or feet.inches (e.g., 5.8, 5.10)");
      return false;
    } else {
      // Centimeter mode
      const cm = parseInt(value);
      if (isNaN(cm)) {
        setMinHeightError("Please enter a valid number");
        return false;
      }

      if (cm < 100 || cm > 250) {
        setMinHeightError("Height should be between 100cm and 250cm");
        return false;
      }

      updateMinHeightInForm(value, "cm");
      setMinHeightError("");
      return true;
    }
  };

  // Handle profession preference input change
  const handleProfPrefInputChange = (e) => {
    setProfPrefInput(e.target.value);
  };

  // Handle location preference input change
  const handleLocPrefInputChange = (e) => {
    setLocPrefInput(e.target.value);
  };

  // Simple add profession preference
  const handleAddProfPref = () => {
    handleArrayAddProfPref();
  };

  // Simple add location preference
  const handleAddLocPref = () => {
    handleArrayAddLocPref();
  };

  // Handle preferred languages change
  const handlePreferredLanguagesChange = (e) => {
    const value = e.target.value;
    if (value.includes(',')) {
      const languagesArray = value.split(',').map(lang => lang.trim()).filter(lang => lang);
      handleInputChange("preferences.preferred_languages", languagesArray);
    } else {
      handleInputChange("preferences.preferred_languages", value ? [value] : []);
    }
  };

  // Format preferred languages for display
  const formatPreferredLanguages = () => {
    if (!formData.preferences?.preferred_languages || !Array.isArray(formData.preferences.preferred_languages)) {
      return "";
    }
    return formData.preferences.preferred_languages.join(", ");
  };

  // ========== SMART DROPDOWN HANDLERS ==========

  // Generic handler for smart dropdowns
  const createSmartDropdownHandlers = (field, setInputFn, setShowOptionsFn) => ({
    handleInputChange: (e) => {
      const value = e.target.value;
      setInputFn(value);
      setShowOptionsFn(true);
      handleInputChange(field, value);
    },
    handleFocus: () => {
      setShowOptionsFn(true);
    },
    handleSelect: (value) => {
      setInputFn(value);
      handleInputChange(field, value);
      setShowOptionsFn(false);
    }
  });

  // Marital status preference handlers
  const maritalStatusPrefHandlers = createSmartDropdownHandlers(
    "preferences.marital_status",
    setMaritalStatusPrefInput,
    setShowMaritalStatusPrefOptions
  );

  // Smoking preference handlers
  const smokingPrefHandlers = createSmartDropdownHandlers(
    "preferences.smoking_preference",
    setSmokingPrefInput,
    setShowSmokingPrefOptions
  );

  // Drinking preference handlers
  const drinkingPrefHandlers = createSmartDropdownHandlers(
    "preferences.drinking_preference",
    setDrinkingPrefInput,
    setShowDrinkingPrefOptions
  );

  // Religion preference handlers
  const religionPrefHandlers = createSmartDropdownHandlers(
    "preferences.religion",
    setReligionPrefInput,
    setShowReligionPrefOptions
  );

  // Country preference handlers
  const countryPrefHandlers = createSmartDropdownHandlers(
    "preferences.country",
    setCountryPrefInput,
    setShowCountryPrefOptions
  );

  // ========== FILTER OPTIONS ==========
  const maritalStatusOptions = ["Any", ...options.marital_statuses];
  const filteredMaritalStatusPrefOptions = maritalStatusOptions.filter(option =>
    option.toLowerCase().includes(maritalStatusPrefInput.toLowerCase())
  );

  const smokingOptions = ["Non-smoker", "Any", ...options.smoking_habits];
  const filteredSmokingPrefOptions = smokingOptions.filter(option =>
    option.toLowerCase().includes(smokingPrefInput.toLowerCase())
  );

  const drinkingOptions = ["Non-drinker", "Any", ...options.drinking_habits];
  const filteredDrinkingPrefOptions = drinkingOptions.filter(option =>
    option.toLowerCase().includes(drinkingPrefInput.toLowerCase())
  );

  const filteredReligionPrefOptions = options.religions.filter(option =>
    option.toLowerCase().includes(religionPrefInput.toLowerCase())
  );

  const filteredCountryPrefOptions = options.countries.filter(option =>
    option.toLowerCase().includes(countryPrefInput.toLowerCase())
  );

  // ========== RENDER SMART DROPDOWN COMPONENT ==========
  const renderSmartDropdown = (label, value, handlers, filteredOptions, showOptions, setShowOptionsFn, containerRef, placeholder = "Type or select from options", required = false) => (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <TextInput
        label={label}
        value={value}
        onChange={handlers.handleInputChange}
        onFocus={handlers.handleFocus}
        placeholder={placeholder}
        fullWidth
        required={required}
      />
      {showOptions && filteredOptions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: '2px'
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={`${option}-${index}`}
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                fontSize: '14px'
              }}
              onClick={() => handlers.handleSelect(option)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ========== RENDER ARRAY INPUT COMPONENT ==========
  const renderArrayInput = (title, label, values, inputValue, onInputChange, onAdd, onRemove, placeholder, fieldName) => (
    <>
      <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8} md={9}>
          <TextInput
            label={label}
            value={inputValue}
            onChange={onInputChange}
            onKeyPress={(e) => e.key === 'Enter' && onAdd()}
            fullWidth
            placeholder={placeholder}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <button
            onClick={onAdd}
            disabled={!inputValue.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: inputValue.trim() ? '#1976d2' : '#cccccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              fontWeight: '500',
              width: '100%',
              height: '56px',
              marginTop: '8px'
            }}
          >
            Add
          </button>
        </Grid>
      </Grid>

      {/* Display added items as chips */}
      {values && values.length > 0 && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {values.map((item, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  padding: '6px 12px',
                  borderRadius: '16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>{item}</span>
                <button
                  onClick={() => onRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#f44336',
                    cursor: 'pointer',
                    fontSize: '18px',
                    padding: '0',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </Grid>
      )}
    </>
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary">
          Partner Preferences
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      {/* Age Preferences */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Minimum Age"
          value={formData.preferences?.min_age || ""}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, '');
            const numValue = parseInt(digitsOnly) || "";
            handleInputChange("preferences.min_age", numValue);
          }}
          required={true}
          type="number"
          InputProps={{ inputProps: { min: 18, max: 80 } }}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Maximum Age"
          value={formData.preferences?.max_age || ""}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, '');
            const numValue = parseInt(digitsOnly) || "";
            handleInputChange("preferences.max_age", numValue);
          }}
          required={true}
          type="number"
          InputProps={{ inputProps: { min: 18, max: 80 } }}
          fullWidth
        />
      </Grid>

      {/* Minimum Height Field */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Minimum Height"
          value={minHeightDisplayValue}
          onChange={handleMinHeightChange}
          onBlur={validateMinHeight}
          error={!!minHeightError}
          helperText={minHeightError}
          placeholder={minHeightUnit === "feet" ? "5.8 or 5'08\"" : "180"}
          inputProps={{
            maxLength: minHeightUnit === "feet" ? 6 : 3
          }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Select
                  value={minHeightUnit}
                  onChange={handleMinHeightUnitChange}
                  size="small"
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '4px 8px',
                      fontSize: '0.875rem',
                      minWidth: '60px'
                    },
                    '& fieldset': { border: 'none' }
                  }}
                >
                  <MenuItem value="feet">Feet</MenuItem>
                  <MenuItem value="cm">cm</MenuItem>
                </Select>
              </InputAdornment>
            )
          }}
        />
      </Grid>

      {/* Education Preference */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Education Preference"
          value={formData.preferences?.education || ""}
          onChange={(e) => handleInputChange("preferences.education", e.target.value)}
          placeholder="e.g., Bachelor's Degree or higher"
          fullWidth
        />
      </Grid>

      {/* Religion Preference Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Religion Preference",
          religionPrefInput,
          religionPrefHandlers,
          filteredReligionPrefOptions,
          showReligionPrefOptions,
          setShowReligionPrefOptions,
          religionPrefRef,
          "Type or select religion preference",
          true
        )}
      </Grid>

      {/* Country Preference Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Country Preference",
          countryPrefInput,
          countryPrefHandlers,
          filteredCountryPrefOptions,
          showCountryPrefOptions,
          setShowCountryPrefOptions,
          countryPrefRef,
          "Type or select country preference",
        )}
      </Grid>

      {/* Sect and Caste Preferences */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="Sect Preference"
          value={formData.preferences?.sect || ""}
          onChange={(e) => handleInputChange("preferences.sect", e.target.value)}
          placeholder="e.g., Sunni, Shia"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label="Caste Preference"
          value={formData.preferences?.caste || ""}
          onChange={(e) => handleInputChange("preferences.caste", e.target.value)}
          placeholder="e.g., Arain, Rajput"
          fullWidth
        />
      </Grid>

      {/* City Preference */}
      <Grid item xs={12} sm={6}>
        <TextInput
          label="City Preference"
          value={formData.preferences?.city || ""}
          onChange={(e) => handleInputChange("preferences.city", e.target.value)}
          placeholder="e.g., Lahore, Karachi"
          fullWidth
        />
      </Grid>

      {/* Marital Status Preference Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Marital Status Preference",
          maritalStatusPrefInput,
          maritalStatusPrefHandlers,
          filteredMaritalStatusPrefOptions,
          showMaritalStatusPrefOptions,
          setShowMaritalStatusPrefOptions,
          maritalStatusPrefRef,
          "Type or select marital status preference",
        )}
      </Grid>

      {/* Smoking Preference Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Smoking Preference",
          smokingPrefInput,
          smokingPrefHandlers,
          filteredSmokingPrefOptions,
          showSmokingPrefOptions,
          setShowSmokingPrefOptions,
          smokingPrefRef,
          "Type or select smoking preference",
          true
        )}
      </Grid>

      {/* Drinking Preference Field */}
      <Grid item xs={12} sm={6}>
        {renderSmartDropdown(
          "Drinking Preference",
          drinkingPrefInput,
          drinkingPrefHandlers,
          filteredDrinkingPrefOptions,
          showDrinkingPrefOptions,
          setShowDrinkingPrefOptions,
          drinkingPrefRef,
          "Type or select drinking preference",
          true
        )}
      </Grid>

      {/* Profession Preferences (Multiple) */}
      <Grid item xs={12}>
        {renderArrayInput(
          "Profession Preferences (Multiple)",
          "Add Profession Preference",
          formData.preferences?.profession_preference,
          profPrefInput,
          handleProfPrefInputChange,
          handleAddProfPref,
          handleArrayRemoveProfPref,
          "e.g., Doctor, Engineer, Teacher",
          "profession_preference"
        )}
      </Grid>

      {/* Location Preferences (Multiple) */}
      <Grid item xs={12}>
        {renderArrayInput(
          "Location Preferences (Multiple)",
          "Add Location Preference",
          formData.preferences?.location_preference,
          locPrefInput,
          handleLocPrefInputChange,
          handleAddLocPref,
          handleArrayRemoveLocPref,
          "e.g., Lahore, Karachi, Islamabad",
          "location_preference"
        )}
      </Grid>

      {/* Preferred Languages Field */}
      <Grid item xs={12}>
        <TextInput
          label="Preferred Languages"
          value={formatPreferredLanguages()}
          onChange={handlePreferredLanguagesChange}
          placeholder="e.g., Urdu, English, Punjabi (separate with commas)"
          multiline
          rows={2}
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
          Document Verification
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={2}>
          <Box sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 1,
            flex: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              CNIC Verified
            </Typography>
            <Typography variant="h6" color={formData.documents_verification?.cnic_verified ? "success.main" : "text.secondary"}>
              {formData.documents_verification?.cnic_verified ? "✓ Yes" : "No"}
            </Typography>
          </Box>

          <Box sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 1,
            flex: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              Education Verified
            </Typography>
            <Typography variant="h6" color={formData.documents_verification?.education_verified ? "success.main" : "text.secondary"}>
              {formData.documents_verification?.education_verified ? "✓ Yes" : "No"}
            </Typography>
          </Box>

          <Box sx={{
            p: 2,
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 1,
            flex: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="text.secondary">
              Employment Verified
            </Typography>
            <Typography variant="h6" color={formData.documents_verification?.employment_verified ? "success.main" : "text.secondary"}>
              {formData.documents_verification?.employment_verified ? "✓ Yes" : "No"}
            </Typography>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default PreferencesStep;